import { useCallback, useEffect, useMemo } from 'react';

import {
  IPharmacistDetails,
  PaymentPlans,
  PharmacyLegendVariant,
  getBookedPharmacistDetails,
  getRequestedLongContractDetails,
  longTermContractRequestDetails,
  pharmacistBookingDetails,
  pharmacyActions,
  pharmacyBillingSelector,
  resetStatus,
  selectPharmacyPreApprove,
  successOrSelector,
  successSelector,
} from '@pharmaplan/common';

import { useAppDispatch } from '../useAppDispatch';
import {
  setBack,
  setButtons,
  setFooterShow,
  setPagination,
} from '../../actions/drawerActions';
import strings from '../../localization';
import { IPharmacistProfile } from '../../components/SelfService/PharmacistProfile/PharmacistProfile';
import { useAppSelector } from '../useAppSelector';
import usePharmacistProfile from './usePharmacistProfile';
import { resetDialog } from '../../reducers/dialogReducer';
import useSelfService from '../useSelfService';

import useBookedProfileActions from './useBookedProfileActions';
import { serverToday } from '../../helpers/Functions';

const { booked, requested } = PharmacyLegendVariant;

const usePharmacyPreRequest = ({
  eventId,
  type,
  eventIds,
  date,
  workshiftId,
  preventInteraction,
  longTermContractId,
  professionalId,
}: IPharmacistProfile) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();

  const { bookingActions, dateTime } = useBookedProfileActions(eventId);

  const {
    approveAction,
    ignoreAction,
    gotoRequestsList,
    gotoWorkshiftDetails,
    showSoftwareWarning,
    handleSoftwareWarning,
    goToBookings,
    comeBackFromRating,
  } = usePharmacistProfile({
    type,
    eventId,
    date,
    eventIds,
    workshiftId,
    professionalId,
    longTermContractId,
    preventInteraction,
  });

  const bookingDetails = useAppSelector(pharmacistBookingDetails);
  const longTermDetails = useAppSelector(longTermContractRequestDetails);

  const details = longTermContractId ? longTermDetails : bookingDetails;

  const preRequest = useAppSelector(selectPharmacyPreApprove);
  const billing = useAppSelector(pharmacyBillingSelector);

  const preApproveSuccess = useAppSelector((state) =>
    successSelector([pharmacyActions.pharmacyPreApprove], state));
  const rejectSuccess = useAppSelector((state) =>
    successOrSelector([pharmacyActions.ignoreRequest, pharmacyActions.rejectLongTermContract], state));

  const { counterOfferId } = details as IPharmacistDetails ?? {};
  const { isSufficientSoftwareKnowledge } = preRequest ?? {};
  const { currentPlan } = billing ?? {};
  const isPast = date < (serverToday()?.toISO({ includeOffset: false }) ?? '');

  const isWorkshiftDetails = type === booked;
  const showButtonsWhen = type === requested;
  const isCounterOffer = counterOfferId && showButtonsWhen;
  const isBasic = currentPlan?.toLowerCase() === PaymentPlans.basic;

  const approveRejectActions = useMemo(() => {
    if (isSelfService && isBasic) {
      return {
        primaryButton: {
          label: strings.confirmAndPay,
          onClick: handleSoftwareWarning,
        },
        secondaryButton: {
          label: strings.reject,
          onClick: ignoreAction,
        },
      };
    }

    return {
      primaryButton: {
        label: strings.confirm,
        onClick: handleSoftwareWarning,
      },
      secondaryButton: {
        label: strings.reject,
        onClick: ignoreAction,
      },
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isCounterOffer, strings, details]);

  const actions = () => {
    if (type === booked) {
      return bookingActions();
    }
    if (type === requested) {
      return approveRejectActions;
    }

    return {
      primaryButton: {
        label: null,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onClick: () => { },
      },
    };
  };

  useEffect(() => {
    if (longTermContractId) {
      dispatch(
        getRequestedLongContractDetails({
          longTermContractId,
          professionalId: professionalId ?? '',
        }),
      );
    } else {
      dispatch(
        getBookedPharmacistDetails({
          id: eventId,
          eventType: type,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      setBack(isWorkshiftDetails ? gotoWorkshiftDetails : gotoRequestsList),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkshiftDetails]);

  useEffect(() => {
    dispatch(
      setPagination({
        show: false,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!preventInteraction && !isPast) {
      dispatch(setFooterShow(true));
      dispatch(
        setButtons({
          show: true,
          ...actions?.(),
        }),
      );
    } else {
      dispatch(setFooterShow(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingActions]);

  const runSoftwareCheck = useCallback(() => {
    if (preApproveSuccess) {
      if (isSufficientSoftwareKnowledge === false) {
        showSoftwareWarning();
      } else if (isSufficientSoftwareKnowledge === true) {
        approveAction();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSufficientSoftwareKnowledge, preApproveSuccess]);

  useEffect(() => {
    runSoftwareCheck();
    dispatch(resetStatus([pharmacyActions.pharmacyPreApprove]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSufficientSoftwareKnowledge, preApproveSuccess]);

  useEffect(() => {
    if (rejectSuccess) {
      dispatch(resetDialog());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejectSuccess]);

  return { showButtonsWhen, isCounterOffer, dateTime, details, goToBookings, comeBackFromRating };
};

export default usePharmacyPreRequest;
